import { TourConfig } from '../models/tour-config.model';
import { TourFeature } from '../config/tour-feature.enum';
import { Audience } from '../config/audience.enum';
import { TourTrigger } from '../config/trigger.enum';
import { AnchorId } from '../config/anchor.registry';

/**
 * Kontextuelle, wiederholbare In-Dialog-Hilfe fuer den Aenderungsantraege-
 * Uebersichts-Dialog (`ChangeRequestsOverviewDialogComponent`). Wird ueber den
 * Hilfe-Button (`<app-dialog-help-button>`) im Dialog-Header manuell gestartet.
 *
 * Erklaert in 7 Schritten: Dialog-Ueberblick → Kennzahlen → Filter/Sortierung →
 * Antrags-Karte (+ Quick-Aktionen) → [Detailansicht oeffnen] Kategorie-Tabs →
 * Vorher/Nachher-Uebersicht → Entscheidung (Genehmigen/Ablehnen + Folgen).
 *
 * Die Detailansicht erreicht der Dialog erst nach Klick auf eine Karte — ein
 * ASYNCHRONER Load (HTTP). Die Tour fuehrt per `onBeforeShow` (synthetischer
 * Klick) dorthin; der 10-s-MutationObserver in
 * `AnchorTrackingService.waitForAnchor` faengt das spaetere Erscheinen der
 * Detail-Anker ab. Pattern analog `SHOW_TIME_RECORDING_DETAILS_HELP_TOUR`
 * (Sub-State per synthetischem Klick) und `openAdvancedAccordion` in
 * `auto-suggest-tutorial.config.ts`.
 *
 * Datenabhaengige Steps tragen `skipIf` auf die tatsaechliche Anchor-Existenz —
 * an einer leeren Liste fallen die Karten-/Detail-Steps sauber weg, ohne leeren
 * Spotlight.
 */

const A = (id: AnchorId): string => `[onboardingAnchor="${id}"]`;

/** Detailansicht aktiv? Der Tabs-Anchor existiert nur im viewMode 'detail'. */
const inDetail = (): boolean =>
  typeof document !== 'undefined' &&
  !!document.querySelector(A(AnchorId.ChangeRequestsTabs));

/** Mindestens eine Antrags-Karte in der Liste vorhanden? */
const hasCard = (): boolean =>
  typeof document !== 'undefined' &&
  !!document.querySelector(A(AnchorId.ChangeRequestsCard));

/**
 * Oeffnet den ersten Antrag (wechselt in die Detailansicht) via synthetischem
 * Klick auf die erste Karte. Idempotent: tut nichts, wenn schon in der Detail-
 * ansicht. Der ausgeloeste HTTP-Load rendert die Detail-Anker verzoegert —
 * `waitForAnchor` wartet darauf.
 */
function openFirstRequest(): void {
  if (inDetail()) return;
  document
    .querySelector<HTMLElement>(A(AnchorId.ChangeRequestsCard))
    ?.click();
}

/**
 * Zurueck in die Listenansicht (fuer Rueckwaerts-Navigation der Tour).
 * Idempotent: klickt den Zurueck-Pfeil nur, wenn aktuell die Detailansicht
 * offen ist. `backToList()` laedt die Liste neu (async) — `waitForAnchor`
 * faengt das erneute Erscheinen der Listen-Anker ab.
 */
function backToListView(): void {
  if (!inDetail()) return;
  document
    .querySelector<HTMLButtonElement>('.overview-dialog .back-button')
    ?.click();
}

/**
 * Stellt die Detailansicht mit aktivem "Uebersicht"-Tab her. Oeffnet zuerst den
 * Antrag (falls noch in der Liste); wechselt bei bereits offener Detailansicht
 * auf einem anderen Tab zurueck auf den ersten Tab ("Uebersicht"). Idempotent.
 */
function showOverviewTab(): void {
  openFirstRequest();
  const firstTab = document.querySelector<HTMLButtonElement>(
    `${A(AnchorId.ChangeRequestsTabs)} .tab-button`
  );
  if (firstTab && !firstTab.classList.contains('active')) {
    firstTab.click();
  }
}

export const CHANGE_REQUESTS_HELP_TOUR: TourConfig = {
  id: TourFeature.ChangeRequestsHelp,
  version: 1,
  title: 'CHANGE_REQUESTS_HELP_TOUR_TITLE',
  description: 'CHANGE_REQUESTS_HELP_TOUR_DESC',
  audience: [Audience.Admin, Audience.TeamLeader],
  triggers: [TourTrigger.Manual],
  // Help-Hub-Start: zur Mitarbeiterliste navigieren, dann den
  // "Aenderungsantraege"-Indikator-Button klicken. Der Button rendert nur
  // bei pendingChangeRequestsCount>0 — ohne offene Antraege schlaegt der
  // Opener-Anchor sauber fehl und der User sieht den
  // `HELP_TOUR_CONTEXT_UNREACHABLE`-Toast.
  launchContext: {
    route: '/employee',
    opens: { openerAnchor: AnchorId.ChangeRequestsButton, settleMs: 350 },
    mode: 'auto',
  },
  statePolicy: {
    allowReplay: true,
    snoozeEnabled: false,
    cooldownHoursAfterDismiss: 0,
    cooldownHoursAfterSnooze: 0,
    suppressAfterCompletion: false,
  },
  priority: 100,
  estimatedDurationSec: 85,
  // Bewusst 'feature-discovery' (nicht 'contextual-help'): klassische Guided
  // Tour mit Spotlight + Voice-Over, nicht die (i)-Icon-HelpMode-Variante.
  // Begruendung ausfuehrlich in auto-suggest-tutorial.config.ts.
  category: 'feature-discovery',
  chapters: [
    {
      id: 'overview',
      title: 'CHANGE_REQUESTS_HELP_TOUR_TITLE',
      steps: [
        {
          // Welcome erklaert den ganzen Dialog — Spotlight am Wrapper (existiert
          // in Liste UND Detail). backToListView sichert die Listenansicht beim
          // Rueckwaerts-Springen.
          id: 'welcome',
          anchor: AnchorId.ChangeRequestsDialog,
          title: 'TUTORIAL.CHANGE_REQUESTS.WELCOME.TITLE',
          content: 'TUTORIAL.CHANGE_REQUESTS.WELCOME.CONTENT',
          placement: 'auto',
          dialogContext: { attachToDialog: true, autoCloseOnDialogClose: true },
          nextCondition: 'manual',
          onBeforeShow: backToListView,
        },
        {
          id: 'stats',
          anchor: AnchorId.ChangeRequestsStats,
          title: 'TUTORIAL.CHANGE_REQUESTS.STATS.TITLE',
          content: 'TUTORIAL.CHANGE_REQUESTS.STATS.CONTENT',
          placement: 'bottom',
          dialogContext: { attachToDialog: true, autoCloseOnDialogClose: true },
          nextCondition: 'manual',
          onBeforeShow: backToListView,
        },
        {
          id: 'filters',
          anchor: AnchorId.ChangeRequestsFilters,
          title: 'TUTORIAL.CHANGE_REQUESTS.FILTERS.TITLE',
          content: 'TUTORIAL.CHANGE_REQUESTS.FILTERS.CONTENT',
          placement: 'bottom',
          dialogContext: { attachToDialog: true, autoCloseOnDialogClose: true },
          nextCondition: 'manual',
          onBeforeShow: backToListView,
        },
        {
          id: 'list',
          anchor: AnchorId.ChangeRequestsCard,
          additionalSpotlightAnchors: [AnchorId.ChangeRequestsCardActions],
          title: 'TUTORIAL.CHANGE_REQUESTS.LIST.TITLE',
          content: 'TUTORIAL.CHANGE_REQUESTS.LIST.CONTENT',
          placement: 'right',
          dialogContext: { attachToDialog: true, autoCloseOnDialogClose: true },
          nextCondition: 'manual',
          onBeforeShow: backToListView,
          skipIf: () => !hasCard(),
        },
        {
          // Oeffnet die Detailansicht (async). waitForAnchor wartet auf
          // .tab-navigation.
          id: 'tabs',
          anchor: AnchorId.ChangeRequestsTabs,
          title: 'TUTORIAL.CHANGE_REQUESTS.TABS.TITLE',
          content: 'TUTORIAL.CHANGE_REQUESTS.TABS.CONTENT',
          placement: 'bottom',
          dialogContext: { attachToDialog: true, autoCloseOnDialogClose: true },
          nextCondition: 'manual',
          onBeforeShow: openFirstRequest,
          skipIf: () => !hasCard() && !inDetail(),
        },
        {
          id: 'comparison',
          anchor: AnchorId.ChangeRequestsOverview,
          title: 'TUTORIAL.CHANGE_REQUESTS.COMPARISON.TITLE',
          content: 'TUTORIAL.CHANGE_REQUESTS.COMPARISON.CONTENT',
          placement: 'auto',
          dialogContext: { attachToDialog: true, autoCloseOnDialogClose: true },
          nextCondition: 'manual',
          onBeforeShow: showOverviewTab,
          skipIf: () => !hasCard() && !inDetail(),
        },
        {
          id: 'decide',
          anchor: AnchorId.ChangeRequestsDecision,
          title: 'TUTORIAL.CHANGE_REQUESTS.DECIDE.TITLE',
          content: 'TUTORIAL.CHANGE_REQUESTS.DECIDE.CONTENT',
          placement: 'top',
          dialogContext: { attachToDialog: true, autoCloseOnDialogClose: true },
          nextCondition: 'manual',
          onBeforeShow: openFirstRequest,
          skipIf: () => !hasCard() && !inDetail(),
          // Letzter Step: kein Zurueck, nur "Abschliessen".
          showPrevButton: false,
        },
      ],
    },
  ],
};
